import {createStyles} from '@mantine/core';
import React, {useMemo} from 'react';
import {Node, ReactFlow, ReactFlowProvider} from 'reactflow';

import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls, {MIN_ZOOM, MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';

import ProcedureRunDesignFlowchartNode, {
  PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
  ProcedureRunDesignFlowchartNodeData,
} from './ProcedureRunDesignFlowchartNode';

const COLUMN_WIDTH = 220;
const ROW_HEIGHT = 96;
const NODE_START_X = 24;
const NODE_START_Y = 24;

const NODE_TYPES = {
  [PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE]: ProcedureRunDesignFlowchartNode,
};

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesConnectable: false,
  nodesDraggable: false,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 0, y: 0, zoom: 1},
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
  proOptions: REACT_FLOW_PRO_OPTIONS,
};

interface ProcedureRunDesignFlowchartProps {
  conditions: Array<BaseCondition>;
  methods: Array<ExperimentMethod>;
  controlsShiftLeft?: number;
  onNodeClick?: () => void;
}

function makeNodes(
  conditions: Array<BaseCondition>,
  methods: Array<ExperimentMethod>
): Array<Node<ProcedureRunDesignFlowchartNodeData>> {
  const nodes: Array<Node<ProcedureRunDesignFlowchartNodeData>> = [];
  let currentRowIndex = 0;

  conditions.forEach((condition, conditionIndex) => {
    for (let replicateIndex = 0; replicateIndex < condition.numReplicates; replicateIndex++) {
      for (let methodIndex = 0; methodIndex < methods.length; methodIndex++) {
        const method = methods[methodIndex];
        nodes.push({
          id: `${conditionIndex}-${replicateIndex}-${method.id}`,
          type: PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
          position: {
            x: NODE_START_X + methodIndex * COLUMN_WIDTH,
            // TODO (BNCH-156222): Row height cannot be static
            y: NODE_START_Y + currentRowIndex * ROW_HEIGHT,
          },
          data: {
            methodName: method.name,
            conditionName: condition.name,
            replicateNumber: replicateIndex + 1,
            conditionDescription: condition.description,
          },
        });
      }
      currentRowIndex += 1;
    }
  });

  return nodes;
}

export default function ProcedureRunDesignFlowchart(props: ProcedureRunDesignFlowchartProps): JSX.Element {
  const {classes} = useStyles();
  const nodes = useMemo(() => makeNodes(props.conditions, props.methods), [props.conditions, props.methods]);

  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <div
          className={classes.flowchartContainer}
          {...getTestProps({component: 'ProcedureRunDesignFlowchart', element: 'root'})}
        >
          <ReactFlow
            nodes={nodes}
            // TODO (BNCH-156156): Add edges
            edges={[]}
            nodeTypes={NODE_TYPES}
            {...STATIC_PROPS}
            onNodeClick={() => {
              props.onNodeClick?.();
            }}
          >
            <HorizontalControls shiftLeft={props.controlsShiftLeft ?? 0} />
            <DefaultBackground />
          </ReactFlow>
          {/* TODO (BNCH-153423): Add empty state for when there are no nodes */}
        </div>
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

const useStyles = createStyles(() => ({
  flowchartContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
}));
